import { z } from "zod";
import { customersApiSchema } from "../services/customersService";
import { productsApiSchema } from "../services/productsService";
import { paginatedResponse } from "../utils";
import { statusApiSchema } from "./status";

const OfferProductSchema = z.object({
    id: z.string(),
    product: productsApiSchema.product,
    discount_percent: z.coerce.number(),
    discount_pln: z.number().nullable(),
    discount_pln_per_sqm: z.number().nullable(),
    price_after_discount: z.number().nullable(),
    listed_price_per_sqm: z.number().nullable(),
    listed_price: z.number().nullable(),
    status: statusApiSchema.status,
});

const OfferCustomerSchema = z.object({
    status: statusApiSchema.status,
    customer: customersApiSchema.shortCustomer,
});

const OfferStatusReasonSchema = z.object({
    id: z.string(),
    reason_name: z.string(),
    reason_name_eng: z.string(),
});

export const OfferSchema = z.object({
    id: z.string(),
    offer_nr: z.string(),
    investment_name: z.string(),
    date_created: z.string(),
    status: statusApiSchema.status,
    status_note: z.string().nullable().optional(),
    email_body: z.string().nullable().optional(),
    email_subject: z.string().nullable().optional(),
    status_reason: OfferStatusReasonSchema.nullable().optional(),
    customers: z.array(OfferCustomerSchema),
    products: z.array(OfferProductSchema),
    is_technical: z.boolean(),
    expire_date: z.string().nullable().optional(),
    draft_id: z.string().uuid().nullable().optional(),
});

const PostCreateOfferRequestSchema = z.object({
    customer_ids: z.array(z.string().uuid()),
    products: z.array(
        z.object({
            product_id: z.string().uuid(),
            discount_percent: z.number().min(-100).max(100),
        }),
    ),
});

const PutUpdateOfferRequestSchema = z.object({
    customer_ids: z.array(z.string().uuid()),
    products: z.array(
        z.object({
            product_id: z.string().uuid(),
            discount_percent: z.number().min(-100).max(100),
        }),
    ),
    expire_date: z.string().optional(),
    draft_id: z.string().uuid().nullable().optional(),
    email_body: z.string().optional().nullable(),
    email_subject: z.string().optional().nullable(),
});

const PostSendOfferRequestSchema = z.object({
    email_subject: z.string().min(1).max(255),
    email_body: z.string().min(1),
    document_ids: z.array(z.string().uuid()),
    email_footer_id: z.string().uuid().nullable().optional(),
    email_addresses: z.array(),
    selected_files: z.array()
});

export const offerCancellationReasonSchema = z.object({
    id: z.string(),
    reason_name: z.string(),
    reason_name_eng: z.string(),
});

export const getOfferCancellationReasonsResponseSchema = z.object({
    results: z.array(offerCancellationReasonSchema),
});

export const GetOffersResponseSchema = paginatedResponse(OfferSchema);

export const offersApiSchema = {
    getOffersResponse: GetOffersResponseSchema,
    postCreateOfferRequest: PostCreateOfferRequestSchema,
    putUpdateOfferRequest: PutUpdateOfferRequestSchema,
    postSendOfferRequest: PostSendOfferRequestSchema,
    offer: OfferSchema,
    offerCustomer: OfferCustomerSchema,
    offerProduct: OfferProductSchema,
    offerStatus: statusApiSchema.status,
    offerStatusReason: OfferStatusReasonSchema,
};
